import React from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

interface BorderBeamProps {
    /**
     * Width of the border beam container
     */
    width?: number | string
    /**
     * Height of the border beam container
     */
    height?: number | string
    /**
     * Border radius of the container
     */
    borderRadius?: number
    /**
     * Duration of one complete rotation in seconds
     */
    duration?: number
    /**
     * Size of the beam segment
     */
    beamSize?: number
    /**
     * Width of the stroke (border thickness)
     */
    strokeWidth?: number
    /**
     * Delay before animation starts (in seconds)
     */
    delay?: number
    /**
     * Color gradient start (blue)
     */
    colorFrom?: string
    /**
     * Color gradient middle (purple)
     */
    colorVia?: string
    /**
     * Color gradient end (blue)
     */
    colorTo?: string
    /**
     * Whether the animation is active
     */
    active?: boolean
    /**
     * Additional className
     */
    className?: string
    /**
     * Children to wrap
     */
    children?: React.ReactNode
}

export function BorderBeam({
    width,
    height,
    borderRadius = 8,
    duration = 2,
    beamSize = 120,
    strokeWidth = 4,
    delay = 0,
    colorFrom = '#3b82f6',
    colorVia = '#8b5cf6',
    colorTo = '#3b82f6',
    active = true,
    className,
    children,
}: BorderBeamProps) {
    const containerRef = React.useRef<HTMLDivElement>(null)
    const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 })
    const animationId = React.useId()

    // Measure container dimensions
    React.useEffect(() => {
        if (!containerRef.current) return

        const updateDimensions = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect()
                if (rect.width > 0 && rect.height > 0) {
                    setDimensions({ width: rect.width, height: rect.height })
                }
            }
        }

        // Initial measurement
        updateDimensions()

        // Use requestAnimationFrame to ensure DOM is ready
        const rafId1 = requestAnimationFrame(() => {
            updateDimensions()
            const rafId2 = requestAnimationFrame(() => {
                updateDimensions()
            })
            return () => cancelAnimationFrame(rafId2)
        })

        // Set up ResizeObserver for dynamic sizing
        const resizeObserver = new ResizeObserver(() => {
            updateDimensions()
        })

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current)
        }

        // Also listen to window resize
        window.addEventListener('resize', updateDimensions)

        return () => {
            cancelAnimationFrame(rafId1)
            resizeObserver.disconnect()
            window.removeEventListener('resize', updateDimensions)
        }
    }, [])

    // Calculate perimeter of rounded rectangle
    const perimeter = React.useMemo(() => {
        const w = dimensions.width || (typeof width === 'number' ? width : 300)
        const h = dimensions.height || (typeof height === 'number' ? height : 40)
        const r = Math.min(borderRadius, w / 2, h / 2)
        return 2 * (w + h) - 8 * r + 2 * Math.PI * r
    }, [dimensions.width, dimensions.height, borderRadius, width, height])

    // Create path for rounded rectangle
    const pathData = React.useMemo(() => {
        const w = dimensions.width || (typeof width === 'number' ? width : 300)
        const h = dimensions.height || (typeof height === 'number' ? height : 40)
        const r = Math.min(borderRadius, w / 2, h / 2)

        if (w <= 0 || h <= 0) return ''

        // Create rounded rectangle path (counter-clockwise)
        return `M ${r} 0 L ${w - r} 0 A ${r} ${r} 0 0 1 ${w} ${r} L ${w} ${h - r} A ${r} ${r} 0 0 1 ${w - r} ${h} L ${r} ${h} A ${r} ${r} 0 0 1 0 ${h - r} L 0 ${r} A ${r} ${r} 0 0 1 ${r} 0 Z`
    }, [dimensions.width, dimensions.height, borderRadius, width, height])

    const style = React.useMemo(() => {
        const s: React.CSSProperties = {}
        if (width !== undefined) s.width = typeof width === 'number' ? `${width}px` : width
        if (height !== undefined) s.height = typeof height === 'number' ? `${height}px` : height
        return s
    }, [width, height])

    return (
        <div
            ref={containerRef}
            className={cn('relative', className)}
            style={style}
        >
            {/* SVG Border Beam */}
            {active && dimensions.width > 0 && dimensions.height > 0 && pathData && perimeter > beamSize && (
                <svg
                    className="absolute inset-0 pointer-events-none overflow-visible"
                    width={dimensions.width}
                    height={dimensions.height}
                    viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                    preserveAspectRatio="none"
                    style={{
                        borderRadius,
                        zIndex: 1
                    }}
                >
                    <defs>
                        {/* Gradient for the animated beam */}
                        <linearGradient id={`gradient-${animationId}`} x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor={colorFrom} stopOpacity="0" />
                            <stop offset="15%" stopColor={colorFrom} stopOpacity="0.8" />
                            <stop offset="50%" stopColor={colorVia} stopOpacity="1" />
                            <stop offset="85%" stopColor={colorTo} stopOpacity="0.8" />
                            <stop offset="100%" stopColor={colorTo} stopOpacity="0" />
                        </linearGradient>

                        {/* Glow filter */}
                        <filter id={`glow-${animationId}`} x="-100%" y="-100%" width="300%" height="300%">
                            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    {/* Animated beam using Framer Motion */}
                    <motion.path
                        d={pathData}
                        fill="none"
                        stroke={`url(#gradient-${animationId})`}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeDasharray={`${beamSize} ${perimeter}`}
                        filter={`url(#glow-${animationId})`}
                        opacity="1"
                        initial={{ strokeDashoffset: beamSize }}
                        animate={{
                            strokeDashoffset: -perimeter + beamSize,
                        }}
                        transition={{
                            duration,
                            delay,
                            repeat: Infinity,
                            ease: 'linear',
                        }}
                    />
                </svg>
            )}

            {/* Children content */}
            <div className="relative z-10">{children}</div>
        </div>
    )
}
